import { User, Mail, CheckCircle, Plus, Settings, Users, Shield, Calendar, LogOut, Trash2 } from 'lucide-react';

interface AccountPageProps {
  user?: {
    id: number;
    email: string;
    name: string;
    picture?: string;
  };
  connectedAccounts?: any[];
  onLogout?: () => void;
  onDisconnectAccount?: (accountId: number) => void;
  onInitiateGoogleLogin?: (isLinkAccount?: boolean) => void;
}

export function AccountPage({ user, connectedAccounts = [], onLogout, onDisconnectAccount, onInitiateGoogleLogin }: AccountPageProps) {
  return (
    <div className="h-full bg-slate-900 text-white">
      {/* Header */}
      <div className="p-6 border-b border-slate-800">
        <h1 className="text-2xl font-bold mb-2">Account</h1>
        <p className="text-slate-400">Manage your account settings and connected services</p>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {/* Multi-Account Status Banner */}
        {connectedAccounts.length > 1 && (
          <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white flex items-center gap-2">
                  Multi-Account Access Enabled
                  <CheckCircle className="w-4 h-4 text-green-400" />
                </h3>
                <p className="text-sm text-slate-300 mt-1">
                  You have {connectedAccounts.length} Google accounts connected. All your emails and documents are searchable across all accounts.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* User Profile */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Profile</h2>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-16 h-16 rounded-full"
                />
              ) : (
                <User className="w-8 h-8 text-slate-400" />
              )}
            </div>
            
            <div>
              <h3 className="font-medium text-white">{user?.name || 'Not signed in'}</h3>
              <p className="text-slate-400 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {user?.email || 'No email available'}
              </p>
            </div>
          </div>
        </div>

        {/* Connected Accounts */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-400" />
              Connected Google Accounts
            </h2>
            {onInitiateGoogleLogin && (
              <button
                onClick={() => onInitiateGoogleLogin(true)}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-colors"
                title="Link another Google account"
              >
                <Plus className="w-4 h-4" />
                Add Account
              </button>
            )}
          </div>
          
          {connectedAccounts.length > 0 ? (
            <div className="space-y-3">
              {connectedAccounts.map((account, index) => (
                <div key={account.id} className="bg-slate-900/50 border border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    {account.picture ? (
                      <img 
                        src={account.picture} 
                        alt={account.name}
                        className="w-12 h-12 rounded-full"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                        {account.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="font-medium text-white flex items-center gap-2">
                        {account.name}
                        {index === 0 && (
                          <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded">Primary</span>
                        )}
                      </h3>
                      <p className="text-sm text-slate-400">{account.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      {connectedAccounts.length > 1 && index !== 0 && onDisconnectAccount && (
                        <button 
                          onClick={() => onDisconnectAccount(account.id)}
                          className="text-slate-400 hover:text-red-400 p-1 transition-colors"
                          title="Disconnect this account"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-slate-700/50">
                    <p className="text-xs text-slate-500">
                      Access to: Gmail, Google Drive, Google Calendar
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-slate-400 flex items-center justify-between">
              <p>No connected accounts</p>
              {onInitiateGoogleLogin && (
                <button
                  onClick={() => onInitiateGoogleLogin(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Account
                </button>
              )}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
          
          <div className="space-y-3">
            <button className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-700 rounded-lg transition-colors">
              <Settings className="w-5 h-5 text-slate-400" />
              <span className="text-white">Account Settings</span>
            </button>
            
            <button className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-700 rounded-lg transition-colors">
              <Calendar className="w-5 h-5 text-slate-400" />
              <span className="text-white">Manage Integrations</span>
            </button>
            
            {onLogout && (
              <button 
                onClick={onLogout}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-red-900/50 rounded-lg transition-colors text-red-400"
              >
                <LogOut className="w-5 h-5" />
                <span>Sign Out</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 